import {
  Badge,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

import { ActionButton, ActionFab, ActionMenuItem } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import {
  FILTER_CONTROL_SIZE,
  FilterBar,
  FilterDateRange,
  FilterField,
  FilterSearch,
  FilterSelect
} from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { FormSelect } from '../../../components/Select'
import ShortText from '../../../components/ShortText'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { ViewOnSiteButton } from '../../../components/ViewOnSiteButton'
import {
  formatDate,
  useHasRole,
  buildEventPreviewUrl,
  isEventPast,
  usePendingRows,
  WEBSITE_URL,
  useAppToast
} from '../../../helpers'
import { EventSeriesService } from '../../../services/Event/EventSeriesService'
import { EventService } from '../../../services/Event/EventService'
import {
  Event,
  EventComplete,
  EventFormat,
  EventListItem,
  EventSeriesComplete,
  EventSeriesListItem,
  EventType
} from '../../../services/Event/types'
import { EventFormModal } from './EventFormModal'
import { EventEditionsModal } from './series/EventEditionsModal'
import { EventSeriesFormModal } from './series/EventSeriesFormModal'
import { SeriesPickerModal } from './series/SeriesPickerModal'

const isSeriesRow = (item: EventListItem): item is EventSeriesListItem => item.kind === 'series'

const formats: Record<EventFormat, string> = {
  IN_PERSON: 'Presencial',
  ONLINE: 'Online',
  HYBRID: 'Híbrido'
}

/** `length` é o tamanho de página e `view` a listagem escolhida — nenhum dos dois é filtro. */
const INITIAL_FILTERS = {
  search: '',
  status: 'all',
  type: undefined as number | undefined,
  date_from: '',
  date_to: '',
  view: 'events' as 'events' | 'series'
}

export default function EventPage() {
  const service = new EventService()
  const seriesService = new EventSeriesService()
  const [isDeleting, setIsDeleting] = useState(false)
  const [seriesToDelete, setSeriesToDelete] = useState<EventSeriesListItem | null>(null)
  const [isDeletingSeries, setIsDeletingSeries] = useState(false)
  const toast = useAppToast()
  const didMount = useRef(false)
  const [events, setEvents] = useState<EventListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [eventTypes, setEventTypes] = useState<EventType[]>([])
  const [filters, setFilters] = useState({ length: DEFAULT_PAGE_SIZE, ...INITIAL_FILTERS })
  // `view` fica de fora: escolhe *o que* a tabela lista (eventos ou séries), não filtra o resultado.
  const isDirty =
    filters.search !== '' ||
    filters.status !== 'all' ||
    filters.type !== undefined ||
    filters.date_from !== '' ||
    filters.date_to !== ''

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilters = (patch: Partial<typeof filters>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }
  const [targetRecord, setTargetRecord] = useState<Event>()
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [recordEditing, setRecordEditing] = useState<EventComplete>()
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [modalKey, setModalKey] = useState(0)
  const [isSeriesModalOpen, setIsSeriesModalOpen] = useState(false)
  const [seriesEditing, setSeriesEditing] = useState<EventSeriesComplete>()
  const [seriesModalKey, setSeriesModalKey] = useState(0)
  const [seriesFocusEditionId, setSeriesFocusEditionId] = useState<number>()
  const [editionsModal, setEditionsModal] = useState<{ id: number; name: string } | null>(null)
  const [attachTarget, setAttachTarget] = useState<Event | null>(null)
  const [seriesFromEvent, setSeriesFromEvent] = useState<EventComplete>()
  const hasRole = useHasRole()
  const permissions = {
    addEvent: hasRole('add_event'),
    editEvent: hasRole('edit_event'),
    toggleEventVisibility: hasRole('toggle_event_visibility'),
    deleteEvent: hasRole('delete_event')
  }

  // A busca já chega debounced do `FilterSearch`; os demais campos aplicam na hora.
  useEffect(() => {
    refreshList()
  }, [filters, page])

  useEffect(() => {
    if (didMount.current) {
      if (!isCreateModalOpen) {
        refreshList()
      }
    } else {
      didMount.current = true
    }
  }, [isCreateModalOpen])

  useEffect(() => {
    if (didMount.current && !isSeriesModalOpen) {
      refreshList()
    }
  }, [isSeriesModalOpen])

  useEffect(() => {
    service.getTypes().then(setEventTypes)
  }, [])

  const handleDeleteEvent = async () => {
    if (!targetRecord) {
      return
    }

    setIsDeleting(true)
    try {
      await service.delete(targetRecord.id)
      toast.success('Evento excluído')
      setOpenDeleteDialog(false)
      refreshList()
    } catch (error) {
      toast.error('Erro ao excluir evento', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const refreshList = async () => {
    setLoading(true)
    setLoadError(false)
    service
      .list({ ...filters, page })
      .then((response) => {
        setEvents(response.data)
        setTotalPages(Math.ceil(response.total_filtered / filters.length))
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar os eventos', error)
      })
      .finally(() => setLoading(false))
  }

  const openEditModal = (id: number) => {
    service.get(id).then((response) => {
      setRecordEditing(response)
      setIsDuplicating(false)
      setIsCreateModalOpen(true)
      setModalKey((k) => k + 1)
    })
  }

  const openDuplicateModal = (id: number) => {
    service.get(id).then((response) => {
      setRecordEditing(response)
      setIsDuplicating(true)
      setIsCreateModalOpen(true)
      setModalKey((k) => k + 1)
    })
  }

  const openCreateSeries = () => {
    setSeriesEditing(undefined)
    setSeriesFromEvent(undefined)
    setSeriesFocusEditionId(undefined)
    setIsSeriesModalOpen(true)
    setSeriesModalKey((k) => k + 1)
  }

  const openEditSeries = (id: number, focusEditionId?: number) => {
    seriesService.get(id).then((response) => {
      setSeriesEditing(response)
      setSeriesFromEvent(undefined)
      setSeriesFocusEditionId(focusEditionId)
      setIsSeriesModalOpen(true)
      setSeriesModalKey((k) => k + 1)
    })
  }

  // Open the series creation form pre-filled from a standalone event; on save the
  // new series is created and this event is attached to it (keeping all its data).
  const handleCreateSeriesFromEvent = async () => {
    if (!attachTarget) return
    try {
      const fullEvent = await service.get(attachTarget.id)
      setAttachTarget(null)
      setSeriesEditing(undefined)
      setSeriesFocusEditionId(undefined)
      setSeriesFromEvent(fullEvent)
      setIsSeriesModalOpen(true)
      setSeriesModalKey((k) => k + 1)
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      toast.error('Não foi possível abrir a criação de série', error)
    }
  }

  const deleteSeries = (item: EventSeriesListItem) => setSeriesToDelete(item)

  const handleDeleteSeries = async () => {
    if (!seriesToDelete) {
      return
    }

    setIsDeletingSeries(true)
    try {
      await seriesService.delete(seriesToDelete.id)
      toast.success('Série excluída')
      setSeriesToDelete(null)
      refreshList()
    } catch (error) {
      toast.error('Não foi possível excluir a série', error)
    } finally {
      setIsDeletingSeries(false)
    }
  }

  const handleAttachToSeries = async (seriesId: number) => {
    if (!attachTarget) return
    try {
      const { message } = await seriesService.attachEvent(seriesId, attachTarget.id)
      toast.success(message ?? 'Evento adicionado à série')
      setAttachTarget(null)
      refreshList()
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      toast.error('Não foi possível adicionar à série', error)
    }
  }

  const savingRow = usePendingRows()

  const toggleVisibility = async (event: Event) => {
    try {
      await service.toggleVisibility(event.id)

      toast.success(`Evento ${event.visible ? 'ocultado' : 'tornado visível'} com sucesso`)

      setEvents((prev) => prev.map((item) => (item.id === event.id ? { ...item, visible: !item.visible } : item)))
    } catch (err) {
      const error = err as AxiosError<{ errors: Record<string, string[]> }>
      toast.error(
        error?.response?.status === 422
          ? 'Verifique se todos os campos foram preenchidos'
          : 'Não foi possível alterar a visibilidade do evento.'
      )
    }
  }

  return (
    <>
      <PageTitle title="Eventos" description="Painel dos eventos da ABRA e suas vendas" />

      <FilterBar onClear={() => updateFilters({ ...INITIAL_FILTERS, view: filters.view })} isDirty={isDirty}>
        <FilterField label="Exibir" maxW="180px">
          {/* Não é um filtro com estado "todos": são duas listagens diferentes, então nada de opção vazia. */}
          <FormSelect
            size={FILTER_CONTROL_SIZE}
            value={filters.view}
            onChange={(value) => updateFilters({ view: value as 'events' | 'series' })}
            options={[
              { value: 'events', label: 'Eventos avulsos' },
              { value: 'series', label: 'Séries de eventos' }
            ]}
            hideEmptyOption
          />
        </FilterField>
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="Buscar por nome"
        />
        <FilterSelect
          label="Visibilidade"
          maxW="150px"
          value={filters.status}
          onChange={(status) => updateFilters({ status })}
          options={[
            { value: 'active', label: 'Visível' },
            { value: 'inactive', label: 'Invisível' }
          ]}
          // "Todos" aqui é um valor de verdade que a API espera, não a ausência de filtro.
          emptyValue="all"
        />
        <FilterSelect
          label="Tipo"
          maxW="180px"
          value={filters.type ?? ''}
          onChange={(value) => updateFilters({ type: parseInt(value) || undefined })}
          options={eventTypes.map((type) => ({ value: type.id, label: type.name }))}
          emptyLabel="Todos os tipos"
        />
        <FilterDateRange
          label="Data"
          value={{ start: filters.date_from, end: filters.date_to }}
          onChange={(date) => updateFilters({ date_from: date.start, date_to: date.end })}
        />
      </FilterBar>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            {filters.view === 'series' ? (
              <Tr>
                <Th>ID</Th>
                <Th>Nome</Th>
                <Th>Formato</Th>
                <Th>Tipos</Th>
                <Th>Atualizado em</Th>
                <Th>Nº de eventos</Th>
                <Th>Ações</Th>
              </Tr>
            ) : (
              <Tr>
                <Th>ID</Th>
                <Th>Nome</Th>
                <Th>Formato</Th>
                <Th>Tipos</Th>
                <Th>Data</Th>
                <Th>Ingressos</Th>
                <Th>Visibilidade</Th>
                <Th>Ações</Th>
              </Tr>
            )}
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={filters.view === 'series' ? 7 : 8} />
            ) : loadError ? (
              <TableErrorRow
                colSpan={filters.view === 'series' ? 7 : 8}
                entity={filters.view === 'series' ? 'séries' : 'eventos'}
                feminine={filters.view === 'series'}
                onRetry={refreshList}
              />
            ) : events.length === 0 ? (
              <TableEmptyRow
                colSpan={filters.view === 'series' ? 7 : 8}
                entity={filters.view === 'series' ? 'série' : 'evento'}
                feminine={filters.view === 'series'}
              />
            ) : filters.view === 'series' ? (
              events.filter(isSeriesRow).map((item) => (
                <Tr key={`series-${item.id}`}>
                  <Td>#{item.id}</Td>
                  <Td>
                    <Text fontWeight="600">
                      <ShortText text={item.name} beforeChars={48} afterChars={0} />
                    </Text>
                  </Td>
                  <Td>{formats[item.format]}</Td>
                  <Td>
                    <Flex gap={1} flexWrap="wrap">
                      {item.types && item.types.length > 0 ? (
                        item.types.map((type) => (
                          <Badge key={type.id} colorScheme="cyan" py={1}>
                            {type.name}
                          </Badge>
                        ))
                      ) : (
                        <Text variant="caption">-</Text>
                      )}
                    </Flex>
                  </Td>
                  <Td>{formatDate(item.updated_at)}</Td>
                  <Td>{item.editions_count}</Td>
                  <Td>
                    <Button
                      size="sm"
                      colorScheme="magenta"
                      variant="outline"
                      mr={2}
                      onClick={() => setEditionsModal({ id: item.id, name: item.name })}
                    >
                      Ver edições
                    </Button>
                    {(permissions.editEvent || permissions.deleteEvent) && (
                      <Menu>
                        <MenuButton as={IconButton} aria-label="Opções" size="sm" variant="ghost">
                          <Icon name="lni lni-line-dotted" transform="rotate(90deg)" />
                        </MenuButton>
                        <MenuList>
                          {permissions.editEvent && (
                            <ActionMenuItem
                              action="edit"
                              label="Editar série"
                              onClick={() => openEditSeries(item.id)}
                            />
                          )}
                          {permissions.deleteEvent && (
                            <ActionMenuItem action="delete" label="Excluir série" onClick={() => deleteSeries(item)} />
                          )}
                        </MenuList>
                      </Menu>
                    )}
                  </Td>
                </Tr>
              ))
            ) : (
              (events.filter((item) => !isSeriesRow(item)) as Event[]).map((event) => (
                <Tr key={event.id}>
                  <Td>{event.id}</Td>
                  <Td>
                    <Link to={`/events/${event.slug}`} style={{ color: '#3182CE' }}>
                      <ShortText text={event.name} beforeChars={40} afterChars={0} />
                    </Link>
                  </Td>
                  <Td>{formats[event.format]}</Td>
                  <Td>
                    <Flex gap={1} flexWrap="wrap">
                      {event.types && event.types.length > 0 ? (
                        event.types.map((type) => (
                          <Badge key={type.id} colorScheme="cyan" py={1}>
                            {type.name}
                          </Badge>
                        ))
                      ) : (
                        <Text variant="caption">-</Text>
                      )}
                    </Flex>
                  </Td>
                  <Td>
                    {formatDate(event.date_start)} {event.time_start}
                  </Td>
                  <Td>
                    {event.total_tickets === 0 ? (
                      <Text variant="caption">-</Text>
                    ) : (
                      <Flex alignItems="center" gap={2} fontSize="sm">
                        <Text fontWeight="600" whiteSpace="nowrap">
                          {event.total_participants}/{event.total_tickets}
                        </Text>
                        <Text color="gray.500" whiteSpace="nowrap">
                          {Math.round((event.total_participants / event.total_tickets) * 100)}%
                        </Text>
                      </Flex>
                    )}
                  </Td>
                  <Td>
                    <Switch
                      isChecked={event.visible}
                      onChange={() => savingRow.run(event.id, () => toggleVisibility(event))}
                      isDisabled={!permissions.toggleEventVisibility || savingRow.isPending(event.id)}
                    />
                  </Td>
                  <Td>
                    {(() => {
                      const past = isEventPast(event)
                      const showLive = event.visible && !past
                      const label = showLive
                        ? 'Ver no site'
                        : past && event.visible
                          ? 'Abrir preview (evento encerrado)'
                          : 'Abrir preview'
                      return (
                        <ViewOnSiteButton
                          href={
                            showLive ? `${WEBSITE_URL}/eventos/${event.slug}` : buildEventPreviewUrl(WEBSITE_URL, event)
                          }
                          label={label}
                          isGhost={!showLive}
                        />
                      )
                    })()}
                    <ActionButton action="view" to={`/events/${event.slug}`} />
                    <ActionButton action="settings" label="Painel do Evento" to={`/events/${event.slug}/admin`} />
                    {(permissions.editEvent || permissions.addEvent || permissions.deleteEvent) && (
                      <Menu>
                        <MenuButton as={IconButton} aria-label="Opções" size="sm" variant="ghost">
                          <Icon name="lni lni-line-dotted" transform="rotate(90deg)" />
                        </MenuButton>
                        <MenuList>
                          {permissions.editEvent && (
                            <ActionMenuItem action="edit" onClick={() => openEditModal(event.id)} />
                          )}
                          {permissions.editEvent && (
                            <MenuItem icon={<Icon name="lni lni-layers" />} onClick={() => setAttachTarget(event)}>
                              Adicionar a uma série
                            </MenuItem>
                          )}
                          {permissions.addEvent && (
                            <ActionMenuItem action="duplicate" onClick={() => openDuplicateModal(event.id)} />
                          )}
                          {permissions.deleteEvent && (
                            <ActionMenuItem
                              action="delete"
                              onClick={() => {
                                setTargetRecord(event)
                                setOpenDeleteDialog(true)
                              }}
                            />
                          )}
                        </MenuList>
                      </Menu>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={filters.length}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, length: pageSize })
          setPage(1)
        }}
      />

      <ConfirmDialog
        isOpen={!!seriesToDelete}
        onClose={() => setSeriesToDelete(null)}
        onConfirm={handleDeleteSeries}
        action="delete"
        entity="série"
        feminine
        name={seriesToDelete?.name}
        warning="Todas as edições da série são excluídas junto."
        isLoading={isDeletingSeries}
      />
      <ConfirmDialog
        isOpen={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteEvent}
        action="delete"
        entity="evento"
        name={targetRecord?.name}
        isLoading={isDeleting}
      />

      {permissions.addEvent && (
        <>
          <ActionFab
            icon={<Icon name="lni lni-layers" />}
            label="Criar série de eventos"
            stackIndex={1}
            onClick={openCreateSeries}
          />
          <ActionFab
            label="Adicionar evento"
            onClick={() => {
              setRecordEditing(undefined)
              setIsCreateModalOpen(true)
              setModalKey((k) => k + 1)
            }}
          />
        </>
      )}

      <EventFormModal
        key={modalKey}
        isOpen={isCreateModalOpen}
        event={recordEditing}
        duplicate={isDuplicating}
        onClose={() => {
          setIsCreateModalOpen(false)
          setIsDuplicating(false)
        }}
      />

      <EventSeriesFormModal
        key={`series-${seriesModalKey}`}
        isOpen={isSeriesModalOpen}
        series={seriesEditing}
        sourceEvent={seriesFromEvent}
        focusEditionId={seriesFocusEditionId}
        onClose={() => setIsSeriesModalOpen(false)}
      />

      {editionsModal && (
        <EventEditionsModal
          isOpen={!!editionsModal}
          seriesId={editionsModal.id}
          seriesName={editionsModal.name}
          canEdit={permissions.editEvent}
          onEditSeries={(editionId) => {
            const id = editionsModal.id
            setEditionsModal(null)
            openEditSeries(id, editionId)
          }}
          onChanged={refreshList}
          onClose={() => setEditionsModal(null)}
        />
      )}

      <SeriesPickerModal
        isOpen={!!attachTarget}
        onClose={() => setAttachTarget(null)}
        onConfirm={handleAttachToSeries}
        onCreateSeries={handleCreateSeriesFromEvent}
        title={attachTarget ? `Adicionar "${attachTarget.name}" a uma série` : 'Adicionar a uma série'}
        confirmLabel="Adicionar à série"
        note="O evento mantém suas configurações, ingressos e inscritos. Apenas o valor mínimo de parcela passa a seguir o padrão da série."
      />
    </>
  )
}
